import { Button, Dialog, DialogContent, DialogTitle, Typography } from "@mui/material";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import React from "react";
import { sleep } from "verdi-common/utils/timeoutUtils";
import { SessionHandler } from "verdi-web-client/APICommands/SessionHandler";
import { OnDemandIrrigation, useOnDemandIrrigationEnabled } from "@/features/cards/zone/on-demand-irrigation";
import { DataViewer } from "@/features/data-viewer/data-viewer";
import { useZoneDataViewerSpec } from "@/features/data-viewer/spec/use-zone-data-viewer-spec";
import { DynamicGraphContext, DynamicGraphContextProvider } from "@/features/dynamic-graph/dynamic-graph-utility";
import FullsizeZoneOverview from "@/features/zone-overview/full-size-zone-overview/fullsize-zone-overview";
import ZoneProfileEditor from "@/features/zone-profile/zone-profile-editor/zone-profile-editor";
import { CLOCK_CADENCE } from "@/providers/wall-clock";
import { ANALYTICS_EVENT, trackEvent } from "@/services/analytics/events";
import { useZoneDevicesPolling } from "@/services/device-polling-service/hooks";
import { FEATURE_FLAGS, useFeatureFlag } from "@/services/feature-flags/feature-flags";
import FocusContext from "@/services/map-management/focus-context";
import GlobalOptions from "@/utils/global-options";
import { ZoneSupplementalDataStreamEditor } from "./zone-supplemental-data-stream-editor";

export const ZoneInfoCardBody = React.memo((props) => {
    const graphContextToProvide = React.useMemo(() => new DynamicGraphContextProvider(), []);
    const [editorOpen, setEditorOpen] = React.useState(false);
    const [dataStreamEditorOpen, setDataStreamEditorOpen] = React.useState(false);
    const isOnDemandIrrigationEnabled = useOnDemandIrrigationEnabled();
    const { isEnabled: isDataViewerEnabled } = useFeatureFlag(FEATURE_FLAGS.DATA_VIEWER);
    const { spec: zoneSpec, isLoading, isError, isEmpty } = useZoneDataViewerSpec(props.Target.id);

    // Poll for devices in the zone at a higher frequency than the default polling frequency
    useZoneDevicesPolling(props.Target.id, isOnDemandIrrigationEnabled);

    React.useEffect(() => {
        // immediately call the async function. This dodges the fact that use effect cannot return a promise.
        (async () => {
            let contextPushed = false;
            let i = 0;
            while (!contextPushed && i < 20) {
                i++;
                if (
                    !FocusContext.contextStack.includes(graphContextToProvide.linkedFocusContext) &&
                    FocusContext.contextStack.includes(props.InfoCard.focusContext)
                ) {
                    FocusContext.pushContextToStack(graphContextToProvide.linkedFocusContext);
                    contextPushed = true;
                }
                await sleep(200);
            }
        })();

        return () => {
            FocusContext.releaseStack(graphContextToProvide.linkedFocusContext);
        };
    }, []);

    return (
        <DynamicGraphContext.Provider value={graphContextToProvide}>
            <List className={"InfoPane_detailList"} key={`ActuallyGenned_InfoCard--${props.Target.UID}`}>
                {/* <StyledEngineProvider injectFirst> */}
                {/*    <ThemeProvider theme={mtheme}> */}
                {isOnDemandIrrigationEnabled && (
                    <div className={"flex flex-col mt-6 mb-2 mx-2 xs:mx-4"}>
                        <OnDemandIrrigation zoneId={props.Target.id} />
                    </div>
                )}
                <ListItem className={"FullsizeZoneOverviewListItem--InfoCard"}>
                    <FullsizeZoneOverview
                        focusContext={props.InfoCard?.focusContext}
                        zone={props.Target}
                        inZoneCard={true}
                        showZoneName={false}
                        showHeader={false}
                        style={{
                            maxWidth: 500,
                            maxHeight: 100,
                        }}
                    />
                </ListItem>
                {isDataViewerEnabled && (
                    <ListItem className={"FullsizeZoneOverviewListItem--InfoCard"}>
                        <div className={"w-full flex flex-col"}>
                            <DataViewer
                                initialSpec={zoneSpec}
                                isLoading={isLoading}
                                isError={isError}
                                isEmpty={isEmpty}
                                autoRefreshCadence={CLOCK_CADENCE.MINUTES_5}
                                resizable={false}
                            />
                        </div>
                    </ListItem>
                )}
                <div className={"FullsizeZoneOverviewListItem--Wrap"}>
                    <ListItem className={"FullsizeZoneOverviewListItem--ListItem"}>
                        <Button
                            onClick={() => {
                                trackEvent(ANALYTICS_EVENT.ZONE_EDIT_CLICK, {
                                    zone_id: props.Target.id,
                                });
                                setEditorOpen(true);
                            }}
                            className={"FullsizeZoneOverviewListItem--ListItem"}
                        >
                            Edit Zone
                        </Button>
                        <ZoneProfileEditor
                            open={editorOpen}
                            onClose={() => {
                                setEditorOpen(false);
                            }}
                            zone={props.Target}
                        />
                    </ListItem>
                    {(SessionHandler.admin || GlobalOptions.enableNonAdminAddingCustomDataStreams) && (
                        <ListItem className={"FullsizeZoneOverviewListItem--ListItem"}>
                            <Button
                                className={"FullsizeZoneOverviewListItem--ListItem"}
                                onClick={() => {
                                    setDataStreamEditorOpen(true);
                                }}
                            >
                                Edit Supplemental Datastreams
                            </Button>
                            <Dialog
                                className={"ZoneProfileEditor"}
                                open={dataStreamEditorOpen}
                                onClose={() => {
                                    setDataStreamEditorOpen(false);
                                }}
                            >
                                <DialogTitle>Edit Supplemental Datastreams</DialogTitle>
                                <DialogContent>
                                    <ZoneSupplementalDataStreamEditor zoneID={props.Target.id} />
                                </DialogContent>
                            </Dialog>
                        </ListItem>
                    )}
                    {SessionHandler.admin && (
                        <ListItem className={"FullsizeZoneOverviewListItem--ListItem"} style={{ marginLeft: "8px" }}>
                            <Typography variant={"subtitle1"} style={{ color: "#757575" }}>
                                {`ID: ${props.Target.id}`}
                            </Typography>
                        </ListItem>
                    )}
                    {SessionHandler.admin && (
                        <ListItem className={"FullsizeZoneOverviewListItem--ListItem"}>
                            <Button
                                className={"DownloadGEOJSON_Button"}
                                onClick={() => {
                                    const template = {
                                        type: "Feature",
                                        geometry: {
                                            type: "Polygon",
                                            coordinates: [props.Target.polygon.map((point) => [point.lng, point.lat])],
                                        },
                                        properties: {
                                            name: props.Target.name,
                                            id: props.Target.id,
                                        },
                                    };
                                    downloadObjectAsJson(template, `${props.Target.name}.geo`);
                                }}
                            >
                                Download GeoJSON
                            </Button>
                        </ListItem>
                    )}
                </div>
                {/* </ThemeProvider> */}
                {/* </StyledEngineProvider> */}
            </List>
        </DynamicGraphContext.Provider>
    );
});

function downloadObjectAsJson(exportObj, exportName) {
    const dataStr = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(exportObj))}`;
    const downloadAnchorNode = document.createElement("a");
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", `${exportName}.json`);
    document.body.appendChild(downloadAnchorNode); // required for firefox
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
}
